import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type {
  TabContent,
  TabContentView,
} from '../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  type ReportPageConfig,
} from '../components/PageConfigProvider/types';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import {
  extractLegacyCoverCopy,
  nestedPagesFromTabContent,
} from '../components/ReportFlow/convert-grid-report-to-flow';
import { pageItems } from '../components/ReportFlow/report-flow-reducer';
import type { ReportFlowLayoutBlock } from '../components/ReportFlow/report-flow-types';
import type { ReportDocumentConfig } from './report-document-types';

const REPORT_DOCUMENT_TAB_ID = 'report';

export const emptyReportTabContent: TabContent = {
  id: REPORT_DOCUMENT_TAB_ID,
  items: [],
  grid: { rows: DEFAULT_GRID_ROWS * 3, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [],
};

export const reportDocumentFromPage = (
  page: ReportPageConfig
): ReportDocumentConfig => {
  const tabContent = page.tabs[0]?.tabContent ?? emptyReportTabContent;
  const coverCopy = extractLegacyCoverCopy(tabContent);

  return {
    id: page.id,
    name: page.name,
    owner: page.owner,
    created: page.created,
    title: tabContent.title || coverCopy.title,
    description: tabContent.description || coverCopy.description,
    primaryFilter: tabContent.primaryFilter,
    secondaryFilters: tabContent.secondaryFilters ?? [],
    dateRangeDefault: tabContent.dateRangeDefault,
    parameters: tabContent.reportParameters,
    pages: nestedPagesFromTabContent(tabContent),
    sampleFilterGroups: page.sampleFilterGroups,
    schemaVersion: 2,
  };
};

const reportGrid = (document: ReportDocumentConfig) => ({
  rows: DEFAULT_GRID_ROWS * Math.max(document.pages.length, 3),
  columns: DEFAULT_GRID_COLUMNS,
});

export const reportTabContentView = (
  document: ReportDocumentConfig
): TabContentView => ({
  id: REPORT_DOCUMENT_TAB_ID,
  grid: reportGrid(document),
  reportParameters: document.parameters,
  primaryFilter: document.primaryFilter,
  secondaryFilters: document.secondaryFilters ?? [],
  dateRangeDefault: document.dateRangeDefault,
  title: document.title,
  description: document.description,
});

export const documentItems = (document: ReportDocumentConfig) =>
  pageItems(document.pages);

const toLayoutRef = (
  block: ReportDocumentConfig['pages'][number]['blocks'][number]
): ReportFlowLayoutBlock =>
  block.kind === 'columns'
    ? { kind: 'columns', itemIds: block.items.map((item) => item.id) }
    : { kind: 'item', itemId: block.item.id };

/** The page-shaped view listings, clone, filter persistence and the shared-report snapshot read. */
export const pageFromReportDocument = (
  document: ReportDocumentConfig,
  items: PositionedItem[] = documentItems(document)
): ReportPageConfig => ({
  id: document.id,
  name: document.name,
  owner: document.owner,
  created: document.created,
  type: PageType.REPORT,
  sampleFilterGroups: document.sampleFilterGroups,
  tabs: [
    {
      id: REPORT_DOCUMENT_TAB_ID,
      label: document.name,
      isActive: true,
      tabContent: {
        ...reportTabContentView(document),
        items,
        grid: reportGrid(document),
        reportFlow: {
          version: 1,
          pages: document.pages.map((page) => ({
            id: page.id,
            blocks: page.blocks.map(toLayoutRef),
          })),
        },
      },
    },
  ],
});

export const withReportDocumentPages = (
  pages: Page[],
  documents: ReportDocumentConfig[]
): Page[] => {
  const documentPages = documents.map((document) =>
    pageFromReportDocument(document)
  );
  const documentIds = new Set(documentPages.map((page) => page.id));
  return [
    ...documentPages,
    ...pages.filter((page) => !documentIds.has(page.id)),
  ];
};
